import {observer} from "mobx-react";
import React from "react";
import {
	AddButtonWhite,
	FilterWrapper,
	Flag,
	ImageCell,
	InfoCell,
	ListWrapper,
	PlayerCardContainer,
	PlayerCardWrapper,
	PlayerData,
	PlayerName,
	PlayerStats,
	PoolWrapper,
	RemoveButtonWhite,
} from "views/components/style/pool";
import {useViewController} from "data/services/locator";
import {Bindings} from "data/constants/bindings";
import {IPoolController} from "views/components/player_pool/pool/player_pool.controller";
import {Input} from "views/components/form";
import {FormControl, InputAdornment, Typography, useMediaQuery} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import {TeamRegion} from "data/enums";
import {eq, partial, round} from "lodash";
import {ReactComponent as IconBlackClose} from "assets/img/icon/close.svg";
import {ReactComponent as IconWhiteClose} from "assets/img/icon/close-white.svg";
import {Exist} from "views/components/exist/exist.component";
import {getPlayerFlag, getPlayerHeadshot} from "data/utils/helper";
import {media} from "assets/css/media";

export const PlayerPool: React.FC = observer(() => {
	const controller = useViewController<IPoolController>(Bindings.PoolController);
	const isMobile = useMediaQuery(`(max-width: ${media.mobile})`);

	const {
		i18n,
		filter,
		playersList,
		handleFormOnChange,
		handleFormSubmit,
		allLineup,
		onAddPlayer,
		onRemovePlayer,
		isCurrentLineupFull,
		selectedRegion,
	} = controller;

	return (
		<PoolWrapper className={"player_pool"} team={selectedRegion}>
			<Typography textAlign="left" variant="h5">
				{i18n.t("pool.title", "Select a golfer of your choice")}
			</Typography>
			<FilterWrapper team={selectedRegion}>
				<form onSubmit={handleFormSubmit} onChange={handleFormOnChange} noValidate={true}>
					<FormControl fullWidth sx={{flexDirection: "column"}}>
						<Input
							value={filter.search}
							variant={"filled"}
							label={i18n.t("form.field.search", "Search")}
							name="search"
							type="text"
							placeholder={i18n.t("form.field.search", "Search")}
							id="your_picks_player_search_field"
							required={false}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<IconButton edge="end">
											<SearchIcon />
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
					</FormControl>
				</form>
			</FilterWrapper>

			<ListWrapper>
				{playersList.map((player, index) => {
					const isSelected = allLineup.includes(player.id);
					const isUsa = eq(player.team, TeamRegion.Usa);

					return (
						<PlayerCardWrapper
							className={player.team}
							key={`${index}_${player.id}`}
							isSelected={isSelected}>
							<PlayerCardContainer>
								<InfoCell>
									<PlayerData>
										<PlayerName>
											<p>{player.firstName}</p>
											<p style={{fontWeight: "500"}}>{player.lastName}</p>
										</PlayerName>
										<Flag>
											<img
												src={getPlayerFlag(player.country)}
												alt="country"
											/>
										</Flag>
									</PlayerData>
									<PlayerStats>
										<p>
											{i18n.t("player.stats.wr", "WR")}
											{":"}
											<span>{player.worldRanking ?? 0}</span>
										</p>
										{isUsa ? (
											<p>
												{i18n.t("player.stats.rc_rank", "RC Rank")}
												{":"}
												<span>{player.stats.RCUSRank}</span>
											</p>
										) : (
											<span>
												<p>
													{i18n.t("player.stats.rc_rank_euro", "RC EU")}
													{":"}
													<span>{player.stats.RCEURank}</span>
												</p>
												<p>
													{i18n.t("player.stats.rc_rank_world", "RC WR")}
													{":"}
													<span>{player.stats.RCEUWorldRank}</span>
												</p>
											</span>
										)}
										<p>
											{i18n.t(
												"player.stats.rc_appearances",
												"RC Appearances"
											)}
											{":"}
											<span>{player.stats.RCAppearances}</span>
										</p>
										<p>
											{i18n.t("player.stats.record", "Record")}
											{":"}
											<span>
												{player.stats.allTimeRecords.wins}
												{"-"}
												{player.stats.allTimeRecords.losses}
												{"-"}
												{player.stats.allTimeRecords.draws}
											</span>
										</p>
										<p>
											{i18n.t("player.stats.picked_by", "Picked By")}
											{":"}
											<span>
												{round(player.stats.pickedBy)}
												{"% of fans"}
											</span>
										</p>
									</PlayerStats>
								</InfoCell>
								<ImageCell className={player.team} isSelected={isSelected}>
									<img src={getPlayerHeadshot(player.id)} alt="player" />
								</ImageCell>
								<Exist when={!isSelected}>
									<AddButtonWhite
										disabled={isCurrentLineupFull}
										onClick={partial(onAddPlayer, player.id, isMobile)}>
										<IconBlackClose />
									</AddButtonWhite>
								</Exist>
								<Exist when={isSelected}>
									<RemoveButtonWhite
										className="remove_btn"
										onClick={partial(onRemovePlayer, player.id, isMobile)}>
										<IconWhiteClose />
									</RemoveButtonWhite>
								</Exist>
							</PlayerCardContainer>
						</PlayerCardWrapper>
					);
				})}
			</ListWrapper>
		</PoolWrapper>
	);
});
